import { useId, useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { encodePath } from '../shared/utils/format';
import { Icon } from './Icon';

/**
 * Jump to a page by name. It filters the revision's own file list (paths only), so it works wherever the
 * wiki can be read and never touches page contents, which the hosted service does not index.
 */
export function PageFinder({ paths, base, rev }: { paths: string[]; base: string; rev: string | null }) {
  const id = useId();
  const listId = `${id}-pages`;
  const navigate = useNavigate();
  const [value, setValue] = useState('');
  const [message, setMessage] = useState('');
  const pages = useMemo(() => paths.filter((path) => /^wiki\/.+\.md$/i.test(path)).sort(), [paths]);
  if (!pages.length) return null;

  const go = () => {
    const wanted = value.trim().toLowerCase();
    if (!wanted) return;
    const exact = pages.find((path) => path.toLowerCase() === wanted);
    const matches = exact
      ? [exact]
      : pages.filter((path) => path.toLowerCase().includes(wanted));
    if (matches.length === 1) {
      setMessage('');
      navigate(`${base}/blob/${encodePath(matches[0]!)}${rev ? `?rev=${encodeURIComponent(rev)}` : ''}`);
    } else {
      setMessage(
        matches.length
          ? `${matches.length} pages match. Choose one from the list.`
          : 'No page has a name like that.',
      );
    }
  };

  return (
    <form
      className="page-finder"
      role="search"
      aria-label="Go to page"
      onSubmit={(event) => {
        event.preventDefault();
        go();
      }}
    >
      <label htmlFor={id}>Go to page</label>
      <div className="row">
        <div className="page-finder-input">
          <Icon name="search" />
          <input
            id={id}
            className="input"
            list={listId}
            value={value}
            autoComplete="off"
            placeholder="Type part of a page name"
            onChange={(event) => {
              setValue(event.target.value);
              setMessage('');
            }}
          />
        </div>
        <button className="btn" type="submit">
          Go
        </button>
      </div>
      <datalist id={listId}>
        {pages.map((path) => (
          <option key={path} value={path} />
        ))}
      </datalist>
      <p className="hint" role="status">
        {message || 'Matches page names only. Page contents are searched on your computer, not here.'}
      </p>
    </form>
  );
}
